/**
 * /campaigns/new — create-campaign form.
 *
 * Holds the brand context (product, audience, offer, proof, default
 * CTA) + brand settings (voice id, model, brand colors, font family).
 * Briefs submitted later inside the campaign inherit all of this.
 *
 * Pre-redesign these fields were spread across the brief form +
 * variant approval flow with no obvious place to set voice/model
 * before TTS ran. The campaign now owns them.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiJson } from '../lib/api';
import {
  DEFAULT_MODEL,
  DEFAULT_VOICE_ID,
  ELEVENLABS_MODELS,
  type ElevenLabsModel,
} from '../lib/types';
import {
  CAMPAIGN_TEMPLATES,
  CATEGORY_LABEL,
  CATEGORY_ORDER,
  templateToFormSeed,
  type CampaignTemplate,
} from '../lib/campaignTemplates';

interface FormState {
  name: string;
  product: string;
  audience: string;
  offer: string;
  proof: string;
  default_cta: string;
  voice_id: string;
  model: ElevenLabsModel;
  primary_color: string;
  background_color: string;
  text_color: string;
  font_family: string;
}

const DEFAULT_STATE: FormState = {
  name: '',
  product: '',
  audience: '',
  offer: '',
  proof: '',
  default_cta: '',
  voice_id: '',
  model: DEFAULT_MODEL,
  primary_color: '#E8432D',
  background_color: '#0D0D0D',
  text_color: '#F2F2F7',
  font_family: 'Inter Tight',
};

interface Campaign {
  id: string;
  name: string;
}

export function NewCampaign() {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(DEFAULT_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);

  function patch<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
    // Don't clear activeTemplateId on edits. "I started from this
    // template" is a permanent fact about the campaign — Pexels seed
    // suggestions stay relevant even if the admin tweaks copy after
    // picking. The template highlight on the card is just visual; the
    // seed-suggestions banner on the Assets tab is the real outcome.
  }

  function applyTemplate(template: CampaignTemplate) {
    const seed = templateToFormSeed(template);
    setForm((current) => ({
      ...current,
      ...seed,
      // Preserve voice_id even when applying a template — admin
      // entered it deliberately; templates don't dictate voice.
      voice_id: current.voice_id,
    }));
    setActiveTemplateId(template.id);
  }

  async function submit() {
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        product: form.product.trim(),
        audience: form.audience.trim(),
        offer: form.offer.trim(),
        proof: form.proof.trim(),
        default_cta: form.default_cta.trim(),
        // Optional fields — send only if non-default / non-empty so the
        // server can fall back to its own defaults where useful.
        voice_id: form.voice_id.trim() || undefined,
        model: form.model || undefined,
        primary_color: form.primary_color || undefined,
        background_color: form.background_color || undefined,
        text_color: form.text_color || undefined,
        font_family: form.font_family.trim() || undefined,
        // Persist the template the admin started from so AssetsTab
        // can show seed-from-Pexels suggestions later, regardless of
        // whether they edited fields or how they navigate back.
        template_id: activeTemplateId ?? undefined,
      };
      const res = await apiJson<{ campaign: Campaign }>('/api/marketing/campaigns', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      // No more auto-seed of the screenshot pack — the screenshots
      // rarely change and admins can re-seed manually from the
      // Assets tab when they actually want them in the matcher pool.
      // Saves ~6-8s on every campaign create.
      const dest = activeTemplateId
        ? `/campaigns/${res.campaign.id}/assets?seed_from=${activeTemplateId}`
        : `/campaigns/${res.campaign.id}/assets`;
      navigate(dest, { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create campaign');
    } finally {
      setSubmitting(false);
    }
  }

  const disabled =
    submitting ||
    !form.name.trim() ||
    !form.product.trim() ||
    !form.audience.trim() ||
    !form.offer.trim() ||
    !form.proof.trim() ||
    !form.default_cta.trim();

  return (
    <div className="marketing-page">
      <header className="marketing-header">
        <div>
          <p className="eyebrow">New campaign</p>
          <h1>Set up brand context</h1>
          <p>
            This information stays with the campaign. Every brief you submit inside it inherits the
            product, audience, voice, and brand colors — no need to re-enter on iteration.
          </p>
        </div>
      </header>

      <section
        aria-labelledby="templates-title"
        style={{ maxWidth: 720, marginBottom: 20 }}
      >
        <h2 id="templates-title" style={{ fontSize: 16, marginBottom: 8 }}>
          Start from a template (optional)
        </h2>
        <p
          style={{
            margin: '0 0 12px 0',
            fontSize: 12,
            color: 'var(--color-text-muted)',
          }}
        >
          Pick a campaign archetype that's close to your audience. The fields below get seeded;
          edit anything you want before creating.
        </p>
        {CATEGORY_ORDER.map((category) => {
          const inCategory = CAMPAIGN_TEMPLATES.filter((t) => t.category === category);
          if (inCategory.length === 0) return null;
          return (
            <div key={category} style={{ marginBottom: 16 }}>
              <h3
                style={{
                  margin: '0 0 8px',
                  fontSize: 12,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: 'var(--color-text-muted)',
                }}
              >
                {CATEGORY_LABEL[category]}
              </h3>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                  gap: 8,
                }}
              >
                {inCategory.map((t) => {
                  const active = t.id === activeTemplateId;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => applyTemplate(t)}
                      style={{
                        textAlign: 'left',
                        padding: 12,
                        borderRadius: 8,
                        border: active
                          ? '2px solid var(--color-primary, currentColor)'
                          : '1px solid var(--color-border, rgba(255,255,255,0.1))',
                        background: active
                          ? 'rgba(232, 67, 45, 0.08)'
                          : 'var(--color-surface-alt)',
                        color: 'inherit',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        fontFamily: 'inherit',
                      }}
                      title={t.blurb}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          justifyContent: 'space-between',
                        }}
                      >
                        <strong style={{ fontSize: 13 }}>{t.label}</strong>
                        {t.primary_color && (
                          <span
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              background: t.primary_color,
                              flexShrink: 0,
                            }}
                            title={`Brand colour ${t.primary_color}`}
                          />
                        )}
                      </div>
                      <p
                        style={{
                          margin: 0,
                          fontSize: 11,
                          color: 'var(--color-text-muted)',
                          lineHeight: 1.4,
                        }}
                      >
                        {t.blurb}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </section>

      <section className="brief-panel" aria-labelledby="campaign-form-title" style={{ maxWidth: 720 }}>
        <h2 id="campaign-form-title">Brand context</h2>
        <BriefField label="Campaign name" value={form.name} onChange={(v) => patch('name', v)} />
        <BriefField label="Product" value={form.product} onChange={(v) => patch('product', v)} />
        <BriefField label="Audience" value={form.audience} onChange={(v) => patch('audience', v)} />
        <BriefField label="Offer" value={form.offer} onChange={(v) => patch('offer', v)} />
        <BriefField label="Proof" value={form.proof} onChange={(v) => patch('proof', v)} />
        <BriefField
          label="Default CTA"
          value={form.default_cta}
          onChange={(v) => patch('default_cta', v)}
        />

        <h2 style={{ marginTop: 24, fontSize: 18 }}>Voice + model</h2>
        <div className="brief-grid">
          <label className="field">
            <span>Voiceover model</span>
            <select
              value={form.model}
              onChange={(e) => patch('model', e.target.value as ElevenLabsModel)}
            >
              {ELEVENLABS_MODELS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Voice override (ID)</span>
            <input
              type="text"
              placeholder={`Default: Emma (${DEFAULT_VOICE_ID})`}
              value={form.voice_id}
              onChange={(e) => patch('voice_id', e.target.value)}
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
            />
          </label>
        </div>

        <h2 style={{ marginTop: 24, fontSize: 18 }}>Brand visuals</h2>
        <div className="brief-grid">
          <label className="field">
            <span>Primary color</span>
            <input
              type="color"
              value={form.primary_color}
              onChange={(e) => patch('primary_color', e.target.value)}
            />
          </label>
          <label className="field">
            <span>Background color</span>
            <input
              type="color"
              value={form.background_color}
              onChange={(e) => patch('background_color', e.target.value)}
            />
          </label>
          <label className="field">
            <span>Text color</span>
            <input
              type="color"
              value={form.text_color}
              onChange={(e) => patch('text_color', e.target.value)}
            />
          </label>
          <label className="field">
            <span>Font family</span>
            <input
              type="text"
              value={form.font_family}
              onChange={(e) => patch('font_family', e.target.value)}
              spellCheck={false}
            />
          </label>
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginTop: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={disabled}
            onClick={submit}
          >
            {submitting ? 'Creating…' : 'Create campaign'}
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={submitting}
            onClick={() => navigate('/campaigns')}
          >
            Cancel
          </button>
        </div>
      </section>
    </div>
  );
}

function BriefField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <textarea value={value} rows={2} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}
